import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Pagination, Button, Grid, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Error from '../Error';
import Spinner from '../Spinner';
import { useGetProfileQuery } from '../../features/api/rest/apiProfileSlice';
import { useGetOrganizationUserQuery } from '../../features/api/rest/apiOrganizationsSlice';
import { useGetRootLotsQuery } from '../../features/api/rest/apiLotsSlice';
import AddLotDialog from './AddLotDialog';
import RootLotCard from './RootLotCard';

function LotsProduct({ ean, opk, searchParamsName = '' }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const modifiedSearchParamsName = searchParamsName ? `${searchParamsName}_` : '';
  const lotsForPage = parseInt(searchParams.get(`${modifiedSearchParamsName}psize`), 10) || 10;
  const lotPage = parseInt(searchParams.get(`${modifiedSearchParamsName}page`), 10) || 1;
  const ordering = searchParams.get(`${modifiedSearchParamsName}ordering`) || '-id';
  const [isAdding, setIsAdding] = useState(false);
  const { data: profile } = useGetProfileQuery();
  const { data: users = [] } = useGetOrganizationUserQuery(opk);

  const { lots, lotsCount, isLoading, isFetching, isError } = useGetRootLotsQuery(
    { ean, opk, ordering, page: lotPage, psize: lotsForPage },
    {
      selectFromResult: (result) => ({
        ...result,
        lots: result.data?.results,
        lotsCount: result.data?.count || 0,
      }),
    }
  );

  let SystemAdmin = false;
  let OrgAdmin = false;
  const permission = () => {
    if (profile.is_staff) {
      SystemAdmin = true;
    }
    OrgAdmin = users.some((user) => user.is_admin && profile.id === user.user);
    if (OrgAdmin || SystemAdmin) return true;
    return false;
  };

  const handlePageChange = (page) => {
    searchParams.set(`${modifiedSearchParamsName}page`, page);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const pagination = <Pagination sx={{ my: 2 }} count={Math.ceil(lotsCount / lotsForPage)} page={lotPage} onChange={(event, newPage) => handlePageChange(newPage)} />;

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;

  return (
    <Box>
      {permission() && (
        <Button
          sx={{ mt: 2 }}
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => {
            setIsAdding(true);
          }}
        >
          {t('organizations.product.lots.addLots.dialog.title')}
        </Button>
      )}
      {lots && Object.keys(lots).length > 0 ? (
        <>
          {pagination}
          <Grid id="Product_Grid">{lots && lots.map((lot) => <RootLotCard key={lot.id} ean={ean} opk={opk} id={lot.id} authority={lot.authority} code={lot.code} from={lot.valid_from} to={lot.valid_to} />)}</Grid>
          {pagination}
        </>
      ) : (
        <Typography variant="h5" sx={{ mt: 3 }}>
          {t('organizations.product.lots.messages.noLots')}
        </Typography>
      )}
      <AddLotDialog action="addRoot" ean={ean} opk={opk} isOpen={isAdding} setIsOpen={setIsAdding} />
    </Box>
  );
}

export default LotsProduct;

LotsProduct.propTypes = {
  ean: PropTypes.string.isRequired,
  opk: PropTypes.string.isRequired,
  searchParamsName: PropTypes.string,
};
